import { useUser } from "@clerk/clerk-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Switch } from "@/components/ui/switch";
import {
  getMarketingEmailPreference,
  optInMarketingEmails,
  optOutMarketingEmails,
} from "@/services/notificationsApi";
import { presentMutationError } from "@/utils/showApiErrorToast";
import SettingCard from "./SettingCard";

/**
 * Marketing preference card — reads/writes Tessera DB via backend.
 * Resend Marketing segment is synced by the API after DB updates.
 */
const MarketingPreferencesCard: React.FC = () => {
  const { user } = useUser();
  const [optedIn, setOptedIn] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const loadGenerationRef = useRef(0);

  const syncClerkMarketingMeta = useCallback(
    async (next: boolean) => {
      if (!user) return;
      try {
        const prev =
          user.unsafeMetadata && typeof user.unsafeMetadata === "object"
            ? { ...(user.unsafeMetadata as Record<string, unknown>) }
            : {};
        await user.update({
          unsafeMetadata: {
            ...prev,
            marketing_opt_in: next,
          },
        });
      } catch (err) {
        // DB is source of truth; Clerk meta is only for signup/webhook alignment.
        console.warn("Could not update Clerk marketing_opt_in metadata:", err);
      }
    },
    [user]
  );

  const refreshFromServer = useCallback(async () => {
    const gen = ++loadGenerationRef.current;
    try {
      const pref = await getMarketingEmailPreference();
      if (gen !== loadGenerationRef.current || savingRef.current) return;
      setOptedIn(pref.optedIn);
    } catch {
      if (gen !== loadGenerationRef.current || savingRef.current) return;
      // Keep prior toggle state if the API is temporarily down.
    } finally {
      if (gen === loadGenerationRef.current && !savingRef.current) {
        setHydrated(true);
      }
    }
  }, []);

  useEffect(() => {
    void refreshFromServer();
  }, [refreshFromServer]);

  const handleToggle = async (checked: boolean) => {
    if (saving || !hydrated) return;
    const previous = optedIn;
    setOptedIn(checked);
    setSaving(true);
    savingRef.current = true;
    try {
      if (checked) {
        await optInMarketingEmails("dashboard_opt_in");
        await syncClerkMarketingMeta(true);
        toast.success("Marketing emails enabled", {
          description: "You'll receive product news and promotional updates.",
        });
      } else {
        await optOutMarketingEmails();
        await syncClerkMarketingMeta(false);
        toast.success("Marketing emails disabled", {
          description:
            "You're unsubscribed from marketing email. Transactional product notifications are unchanged.",
        });
      }
      try {
        const pref = await getMarketingEmailPreference();
        setOptedIn(pref.optedIn);
      } catch {
        // Keep local checked after successful mutate.
      }
    } catch (error: unknown) {
      setOptedIn(previous);
      presentMutationError(error, "Couldn't update marketing preference");
    } finally {
      savingRef.current = false;
      setSaving(false);
      setHydrated(true);
    }
  };

  return (
    <SettingCard
      title="Marketing preferences"
      description="Choose whether Tessera can send you marketing and promotional emails."
      rightContent={
        <Switch
          checked={optedIn}
          disabled={!hydrated || saving}
          aria-busy={saving || undefined}
          aria-label="Receive marketing emails"
          onCheckedChange={(checked) => void handleToggle(checked)}
        />
      }
    />
  );
};

export default MarketingPreferencesCard;
